import React, { Component } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Stars from '../Stars'
import { Box, Truncate, Label, Text, Flex, Checkbox, Link, SrOnly, getPaletteColor } from 'pcln-design-system'

const propTypes = {
  primaryLabel: PropTypes.string,
  primaryLabelIcon: PropTypes.node,
  primaryIcon: PropTypes.node,
  primaryIconAltText: PropTypes.string,
  secondaryLabel: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  subtitle: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  value: PropTypes.string,
  repeatIconByFactor: PropTypes.number,
  iconPrimaryColor: PropTypes.string,
  iconSecondaryColor: PropTypes.string,
  checkboxId: PropTypes.string,
  disabled: PropTypes.bool,
  checked: PropTypes.bool,
  hasOnlyFilter: PropTypes.bool,
  handleOnlyFilter: (props, propName) => {
    if (!!props.hasOnlyFilter && typeof props[propName] !== 'function') {
      return new Error(`please provide a function to 'handleOnlyFilter' if 'hasOnlyFilter' props is true`)
    }
  },
  wrapLabel: PropTypes.bool,
  shouldBoldLabel: PropTypes.bool,
  dataAutobotElementId: PropTypes.string,
  shouldPadConsistently: PropTypes.bool,
  tabIndex: PropTypes.number,
  title: PropTypes.string,
  isAccordionOpen: PropTypes.bool,
  primaryTextStyle: PropTypes.object,
  secondaryTextStyle: PropTypes.object,
}

const defaultProps = {
  disabled: false,
  checked: false,
  wrapLabel: false,
  primaryLabelIcon: '',
  hasOnlyFilter: false,
  shouldBoldLabel: false,
  shouldPadConsistently: false,
  primaryTextStyle: { color: 'text.base', bold: false },
  secondaryTextStyle: { color: 'text.base', bold: false },
}

const StyledLabel = styled(Label)`
  cursor: pointer;
  line-height: 1.5;
  vertical-align: baseline;
  user-select: none;
  padding-top: ${(props) => props.theme.space[2]}px;
  padding-bottom: ${(props) => props.theme.space[2]}px;
  color: ${(props) => getPaletteColor('text', props.disabled ? 'light' : 'base')};

  ${(props) =>
    props.shouldPadConsistently
      ? ''
      : `
      &:first-of-type {
        padding-top: ${props.theme.space[1]}px;
      }
      &:last-of-type {
        padding-bottom: ${props.theme.space[1]}px;
      }
    `}

  /* 64em - 1024px */
  ${(props) => props.theme.mediaQueries.xl} {
    padding-top: ${(props) => props.theme.space[1]}px;
    padding-bottom: ${(props) => props.theme.space[1]}px;
  }

  &:hover {
    background-color: ${(props) =>
      props.disabled || props.hasOnlyFilter ? '' : getPaletteColor('background.light')};

    .pcln-checkbox-only-filter {
      opacity: 1;
    }

    & svg:not(.primary-star):not(.secondary-star):not(.pcln-filters-primary-checkbox-icon) {
      color: ${(props) => (props.disabled ? '' : getPaletteColor('primary.base'))};
    }
  }
`

const LabelImage = styled(Box)`
  display: inline;
  vertical-align: middle;
  color: ${(props) => getPaletteColor('text', props.disabled ? 'light' : 'base')};
  margin-right: 8px;
`

// TODO: update pcln-design-system Checkbox to remove extra height;
const CheckboxWrapper = styled(Box)`
  > div {
    height: 24px;
    padding: 0;
  }
`

const OnlyFilterLink = styled(Link)`
  flex: 0 0 auto;
  text-align: left;
  display: block;
  opacity: 0;
  transition: opacity 200ms ease-out;
  padding-top: ${(props) => props.theme.space[1]}px;
  margin-right: ${(props) => props.theme.space[2]}px;

  &:hover {
    text-decoration: underline;
  }
`

const PrimaryText = styled(Text.span)`
  display: ${(props) => (props.wrapLabel ? 'block' : 'inline')};
`

const SecondaryText = styled(Text.span)`
  flex: 1 1 auto;
`

class CheckboxFilter extends Component {
  constructor(props) {
    super(props)
    this.state = {
      checked: this.props.checked,
    }
    this._updateSelection = this.updateSelection.bind(this)
    this._onlyHandler = this.onlyHandler.bind(this)
  }

  updateSelection() {
    if (!this.props.disabled && this.props.isAccordionOpen !== false) {
      this.setState({
        checked: !this.state.checked,
      })
    }
  }

  onlyHandler(event) {
    event.preventDefault()
    const { checkboxId, value } = this.props
    this.props.handleOnlyFilter({
      element: event.target,
      checkboxId,
      value,
    })
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    this.setState({
      checked: newProps.checked,
    })
  }

  render() {
    if (!this.props.repeatIconByFactor && !this.props.primaryLabel) {
      return null
    }

    const {
      wrapLabel,
      shouldBoldLabel,
      hasOnlyFilter,
      disabled,
      tabIndex,
      title,
      isAccordionOpen,
      primaryTextStyle,
      secondaryTextStyle,
    } = this.props
    const LabelContent = wrapLabel ? Text.span : Truncate

    const isDisabled = disabled || isAccordionOpen === false

    return (
      <StyledLabel
        data-testid='checkbox-filter'
        disabled={isDisabled}
        tabIndex={tabIndex}
        htmlFor={this.props.checkboxId}
        hasOnlyFilter={hasOnlyFilter}
        fontSize={0}
        fontWeight={shouldBoldLabel ? 'bold' : 'regular'}
        data-autobot-element-id={this.props.dataAutobotElementId}
        shouldPadConsistently={this.props.shouldPadConsistently}
      >
        <Flex align='flex-start' alignItems='flex-start' justify='flex-start' justifyContent='flex-start'>
          <CheckboxWrapper>
            <Checkbox
              height='24px'
              id={this.props.checkboxId}
              value={this.props.value}
              size={24}
              disabled={isDisabled}
              tabIndex={tabIndex}
              checked={this.state.checked}
              onChange={this._updateSelection}
            />
          </CheckboxWrapper>
          <LabelContent
            aria-label={this.props.primaryLabel}
            mr={hasOnlyFilter ? 1 : 2}
            pt={1}
            htmlFor={this.props.checkboxId}
            style={{ flex: '0 1 auto' }}
          >
            <PrimaryText ml={2} title={wrapLabel ? '' : title} wrapLabel={wrapLabel} {...primaryTextStyle}>
              {!this.props.repeatIconByFactor &&
                this.props.primaryLabelIcon &&
                typeof this.props.primaryLabelIcon !== 'string' &&
                React.cloneElement(this.props.primaryLabelIcon, {
                  className: 'pcln-filters-primary-checkbox-icon',
                  disabled: isDisabled,
                  tabIndex: tabIndex,
                  size: 16,
                  mr: 2,
                  mb: 1,
                  id: this.props.checkboxId + '-icon',
                  color: this.props.disabled ? 'text.light' : 'text.base',
                  style: { 'vertical-align': 'middle' },
                })}
              {this.props.repeatIconByFactor && this.props.primaryLabelIcon && (
                <Text.span
                  aria-label={`${this.props.repeatIconByFactor} star${
                    this.props.repeatIconByFactor === 1 ? '' : 's'
                  }`}
                  mr={2}
                >
                  <Stars
                    className='pcln-filters-primary-checkbox-icon'
                    icon={this.props.primaryLabelIcon}
                    iconPrimaryColor={this.props.iconPrimaryColor}
                    iconSecondaryColor={this.props.iconSecondaryColor}
                    stars={this.props.repeatIconByFactor}
                  />
                </Text.span>
              )}
              {this.props.primaryIcon && (
                <LabelImage disabled={isDisabled}>{this.props.primaryIcon}</LabelImage>
              )}
              {this.props.primaryIconAltText && <SrOnly>{this.props.primaryIconAltText}</SrOnly>}
              {this.props.primaryLabel}
            </PrimaryText>
          </LabelContent>
          {hasOnlyFilter && !isDisabled && (
            <OnlyFilterLink className='pcln-checkbox-only-filter' onClick={this._onlyHandler} color='primary'>
              {'only'}
            </OnlyFilterLink>
          )}
          <SecondaryText pt={1} align='right' textAlign='right' {...secondaryTextStyle}>
            {typeof this.props.secondaryLabel === 'number' && `(${this.props.secondaryLabel})`}
            {typeof this.props.secondaryLabel === 'string' && this.props.secondaryLabel}
          </SecondaryText>
        </Flex>
        {this.props.subtitle && (
          <Flex style={{ position: 'relative', alignItems: 'center' }} ml={1}>
            <Text fontSize={0} color='text.light' ml={4}>
              {this.props.subtitle}
            </Text>
          </Flex>
        )}
      </StyledLabel>
    )
  }
}

CheckboxFilter.propTypes = propTypes
CheckboxFilter.defaultProps = defaultProps

export default CheckboxFilter
